/* eslint-disable */
import { cloneDeep, differenceBy, isEqual, pick } from 'lodash';
import {
    queryCreate,
    queryFilterGetInfo,
    querySortGetInfo,
    querySortCreate,
    queryFilterCreate,
    queryFilterAdd,
    queryFilterExists,
    queryFilterRemove,
    queryFilterRemoveAll,
    querySortAdd,
    querySortExists,
    querySortRemove,
    querySortRemoveAll,
    queryReplaceFilterPlaceholders,
    queryFormatFiltersValues,
    queryVariableGetInfo,
    queryVariableCreate,
    queryVariableUpdate,
    queryVariableAdd,
    queryMetricCustomAdd,
    queryMetricCustomExists,
    queryMetricCustomCreate,
    queryMetricCustomRemove,
    queryMetricCustomRemoveAll
} from '../utils/query-utils';
import { QueryKey, QueryKeyRuntime } from '../utils/query-const';

import { DrilldownService } from './drilldown';
import { QueryModelValidator } from './query-model-validator';

const normalizeVariableName = (name) => name.replace('@', '');
const prefixVariableName = (name) => `@${name}`;

/**
 * @typedef {import('@goodt-common/data').IDatasetQuery} IDatasetQuery
 * @typedef {import('@goodt-common/data').IDatasetQueryMetricCustom} IDatasetQueryMetricCustom
 * @typedef {import('@goodt-common/data').IQueryMetricCustomInfo} IQueryMetricCustomInfo
 * @typedef {import('@goodt-common/data').IQueryVariableInfo} IQueryVariableInfo
 * @typedef {import('@goodt-common/data').IQueryFilterInfo} IQueryFilterInfo
 * @typedef {import('@goodt-common/data').IQuerySortInfo} IQuerySortInfo
 * @typedef {import('@goodt-common/data').IQueryModelStrategy} IQueryModelStrategy
 */

export class QueryModelBase {
    /**
     * @type {IDatasetQuery}
     */
    _query;

    /**
     *
     * @param {IDatasetQuery} query
     * @param clone
     */
    constructor(query, { clone = true } = {}) {
        this._query = clone === true ? queryCreate(query) : query;
        this._validator = new QueryModelValidator(this);
    }

    /**
     *
     * @return {IDatasetQuery}
     */
    get raw() {
        return cloneDeep(this._query);
    }

    /**
     * @return {string}
     */
    get from() {
        return this._query[QueryKey.FROM];
    }

    /**
     * @param {string} from
     */
    set from(from) {
        this._query[QueryKey.FROM] = from;
    }

    /**
     * @return {string[]}
     */
    get fields() {
        return [...this._query[QueryKey.FIELDS]];
    }

    /**
     * @param {string[]} fields
     */
    set fields(fields) {
        this._query[QueryKey.FIELDS] = [...new Set(fields)];
    }

    /**
     * @return {string[]}
     */
    get metrics() {
        return [...this._query[QueryKey.METRICS]];
    }

    /**
     * @param {string[]} metrics
     */
    set metrics(metrics) {
        this._query[QueryKey.METRICS] = [...new Set(metrics)];
        this._ensureOrder();
    }

    /**
     * @return {string[]}
     */
    get dimensions() {
        return [...this._query[QueryKey.DIMENSIONS]];
    }

    /**
     * @param {string[]} dimensions
     */
    set dimensions(dimensions) {
        this._query[QueryKey.DIMENSIONS] = [...new Set(dimensions)];
        this._ensureOrder();
    }

    /**
     * @return {IQueryFilterInfo[]}
     */
    get filters() {
        return this._query[QueryKey.FILTERS].map(queryFilterGetInfo);
    }

    /**
     *
     * @param {IQueryFilterInfo[]} filterInfos
     */
    set filters(filterInfos) {
        this._query[QueryKey.FILTERS] = filterInfos.map(queryFilterCreate);
    }

    /**
     * @return {IQueryVariableInfo[]}
     */
    get variables() {
        return this._query[QueryKey.VARIABLES].map(queryVariableGetInfo);
    }

    /**
     *
     * @param {Partial<IQueryVariableInfo>[]} variableInfos
     */
    set variables(variableInfos) {
        this._query[QueryKey.VARIABLES] = variableInfos.map(queryVariableCreate);
    }

    /**
     * @return {string[]}
     */
    get variableNames() {
        return this.variables.map(({ name }) => prefixVariableName(name));
    }

    /**
     * @return {IQuerySortInfo[]}
     */
    get sort() {
        return this._query[QueryKey.SORT].map(querySortGetInfo);
    }

    /**
     *
     * @param {IQuerySortInfo[]} sortInfos
     */
    set sort(sortInfos) {
        this._query[QueryKey.SORT] = sortInfos.map(querySortCreate)
    }

    /**
     *
     * @return {import('@goodt-common/data').IDatasetQueryDrilldown}
     */
    get drilldowns() {
        return cloneDeep(this._query[QueryKeyRuntime.DRILLDOWNS]) ?? [];
    }

    set drilldowns(drilldowns) {
        this._query[QueryKeyRuntime.DRILLDOWNS] = drilldowns;
        this._ensureOrder();
    }

    /**
     *
     * @return {string[]}
     */
    get order() {
        const order = this._query[QueryKey.ORDER] ?? [];
        // prettier-ignore
        return order.length > 0
            ? [...order]
            : [...this._defaultOrder];
    }

    /**
     *
     * @param {string[]} order
     */
    set order(order) {
        this._query[QueryKey.ORDER] = [...new Set([...order])];
        this._ensureOrder();
    }

    /**
     *
     * @return {string[]}
     * @private
     */
    get _defaultOrder() {
        const { dimensions, metrics, drilldowns } = this;
        const drilldownNames = drilldowns.map(({ name }) => name);
        const drilldownDimensions = drilldowns.flatMap(({ dimensions }) => dimensions);
        const freeDimensions = dimensions.filter((dimension) => drilldownDimensions.includes(dimension) === false);
        
        return [...drilldownNames, ...freeDimensions, ...metrics];
    }

    /**
     *
     * @private
     */
    _ensureOrder() {
        const { order, _defaultOrder } = this;
        if (isEqual(order, _defaultOrder)) {
            return;
        }
        this._query[QueryKey.ORDER] = [
            ...new Set([
                ...order.filter((name) => _defaultOrder.includes(name)),
                // extend with mandatory 'dimensions', 'metrics'
                ..._defaultOrder
            ])
        ];
    }

    validate() {
        return this._validator.validate();
    }
}

export class QueryModelExtended extends QueryModelBase {
    /**
     *
     * @param {import('@goodt-common/data').IQueryFilterInfoIQueryFilterInfo | string} filter
     * @return {boolean}
     */
    hasFilter(filter) {
        return queryFilterExists({ query: this._query, filter });
    }

    /**
     *
     * @param {IQuerySortInfo | string} sort
     * @return {boolean}
     */
    hasSort(sort) {
        return querySortExists({ query: this._query, sort });
    }

    /**
     *
     * @return {import('@goodt-common/data').IQueryFilterInfo}
     */
    createBlankFilter() {
        return { name: '', operator: '', value: [] };
    }

    /**
     *
     * @return {import('../utils').IQuerySortInfo}
     */
    createBlankSort() {
        return { name: '', direction: '' };
    }

    /**
     * @param {string} param
     * @return {string}
     */
    getParamName(param) {
        const { metrics, dimensions, fields } = this;
        return [...metrics, ...dimensions, ...fields].includes(param) ? param : null;
    }

    /**
     *
     * @param {IDatasetQuery} query
     * @return {Partial<IDatasetQuery>}
     * @private
     */
    _build(query) {
        // eslint-disable-next-line no-param-reassign
        query = queryFormatFiltersValues({ query });
        // eslint-disable-next-line no-param-reassign
        query = queryReplaceFilterPlaceholders({ query });

        return pick(query, Object.values(QueryKey));
    }

    /**
     *
     * @return {Partial<IDatasetQuery>}
     */
    get prepared() {
        return this._build(this.raw);
    }

    /**
     * @param {IQueryFilterInfo['name']} name
     * @return {IQueryFilterInfo | undefined}
     */
    filterGetInfo(name) {
        return this.filters.find((info) => info.name === name);
    }

    /**
     * @param {import('@goodt-common/data').IQueryFilterInfo} filter
     * @param {boolean} [forceUpdate=true]
     * @returns {boolean}
     */
    filterAdd({ name, operator, value }, forceUpdate = true) {
        const { _query: query } = this;
        try {
            const filter = queryFilterCreate({ name, operator, value });
            this._query = queryFilterAdd({ query, filter }, forceUpdate);
            return true;
        } catch {
            return false;
        }
    }

    /**
     * @param {IQueryFilterInfo['name']} name
     * @returns {boolean}
     */
    filterRemove(name) {
        const { _query: query } = this;
        if (queryFilterExists({ query, filter: name })) {
            this._query = queryFilterRemove({ query, filter: name });
            return true;
        }
        return false;
    }

    /**
     * @returns {void}
     */
    filterRemoveAll() {
        const { _query: query } = this;
        this._query = queryFilterRemoveAll({ query });
    }

    /**
     * @param {IQuerySortInfo} sort
     * @param {boolean} [forceUpdate=true]
     * @returns {void}     */
    sortAdd({ name, direction }, forceUpdate = true) {
        const { _query: query } = this;
        const sort = querySortCreate({ name, direction });
        this._query = querySortAdd({ query, sort }, forceUpdate);
    }

    /**
     * @param {IQuerySortInfo['name']} name
     * @returns {void}
     */
    sortRemove(name) {
        const { _query: query } = this;
        if (querySortExists({ query, sort: name })) {
            this._query = querySortRemove({ query, sort: name });
        }
    }

    /**
     * @returns {void}
     */
    sortRemoveAll() {
        const { raw: query } = this;
        this._query = querySortRemoveAll({ query });
    }

    /**
     * @param {IQueryVariableInfo['name']} name
     * @return {IQueryVariableInfo | undefined}
     */
    variableGetInfo(name) {
        name = normalizeVariableName(name);
        return this.variables.find((info) => info.name === name);
    }

    /**
     * @param {IQueryVariableInfo} queryVariableInfo
     * @param {boolean} [forceUpdate=true]
     * @returns {void}
     */
    variableAdd(queryVariableInfo, forceUpdate = true) {
        const { _query: query } = this;
        const { name, value } = queryVariableInfo;
        const variable = queryVariableCreate({
            name: normalizeVariableName(name),
            value
        });
        this._query = queryVariableAdd({ query, variable }, forceUpdate);
    }

    /**
     * @param {IQueryVariableInfo['name']} name
     * @returns {boolean}
     */
    variableRemove(name) {
        const { _query: query } = this;
        // do not actually remove from 'query' just set new query variable with 'null' value
        const currentVariableInfo = this.variableGetInfo(name);
        const emptyVariableInfo = {
            name: normalizeVariableName(name),
            value: null
        };

        if (isEqual(currentVariableInfo, emptyVariableInfo)) {
            return false;
        }

        this._query = queryVariableUpdate({ query, variable: queryVariableCreate(emptyVariableInfo) });
        return true;
    }
    
    /**
     *
     * @return {{[p: string]: string}}
     */
    createResultQueryParamMap() {
        return Object.fromEntries(this.order.map((param) => [param, param]));
    }
}

export class QueryModel extends QueryModelExtended {
    /**
     * @type {DrilldownService}
     * @private
     */
    _drilldown;

    /**
     *
     * @param {IDatasetQuery} query
     */
    constructor(query) {
        super(query);
        this._initDrilldown();
    }

    /**
     * @return {DrilldownService}
     */
    get drilldown() {
        return this._drilldown;
    }

    get dimensions() {
        const { drilldown } = this;
        if (drilldown.isActive === false) {
            return super.dimensions;
        }
        // prettier-ignore
        return super.dimensions
            .map((dimension) =>
                drilldown.activeDimensions.includes(dimension)
                    ? drilldown.getNameByDimension(dimension)
                    : dimension
            )
            .filter((dimension) => drilldown.dimensions.includes(dimension) === false);
    }

    set dimensions(dimensions) {
        super.dimensions = dimensions;
    }

    /**
     *
     * @return {import('@goodt-common/data').IDatasetQueryDrilldown}
     */
    get drilldowns() {
        return super.drilldowns;
    }

    set drilldowns(drilldowns) {
        super.drilldowns = drilldowns;
        this._initDrilldown();
    }

    /**
     *
     * @return {IDatasetQuery}
     */
    get raw() {
        return this.drilldown.isActive ? this._raw : super.raw;
    }

    /**
     *
     * @return {IDatasetQuery}
     * @private
     */
    get _raw() {
        const { drilldown } = this;
        const filters = [...differenceBy(this.drilldown.filters, this.filters, 'name'), ...this.filters];
        // prettier-ignore
        const dimensions = super.dimensions
            .filter((dimension) =>
                drilldown.dimensions.includes(dimension)
                    ? drilldown.activeDimensions.includes(dimension)
                    : true
            );

        // filter drilldown sorts active dimensions
        const sorts = this._query[QueryKey.SORT].filter((sort) => {
            const { name } = querySortGetInfo(sort);
            if (drilldown.dimensions.includes(name) === false) {
                return true;
            }
            return drilldown.activeDimensions.includes(name);
        });

        return {
            ...this._query,
            [QueryKey.DIMENSIONS]: dimensions,
            [QueryKey.SORT]: sorts,
            [QueryKey.FILTERS]: filters.map(queryFilterCreate)
        };
    }

    _initDrilldown() {
        this._drilldown = new DrilldownService(this.drilldowns);
    }

    /**
     *
     * @return {import('@goodt-common/data').QueryModelBase}
     */
    getBaseModel() {
        return new QueryModelBase(this._query);
    }

    /**
     * @overrides
     * @param {string} param
     * @return {string}
     */
    getParamName(param) {
        if (this.drilldown.isActive === false) {
            return super.getParamName(param);
        }
        if (this.drilldown.has(param)) {
            return this.drilldown.getDimension(param);
        }
        if (this.drilldown.dimensions.includes(param)) {
            return param;
        }
        return super.getParamName(param);
    }

    /**
     *
     * @return {{[p: string]: string}}
     */
    createResultQueryParamMap() {
        // prettier-ignore
        return Object.fromEntries(
            this.order.map((param) => [param, this.getParamName(param)])
        );
    }
}

export class QueryModelWithMetricsCustom extends QueryModel {
    /**
     *
     * @param {IDatasetQuery} query
     */
    constructor(query) {
        super(query);
        this._query[QueryKey.METRICS_CUSTOM] ??= [];
    }
    /**
     * @return {IDatasetQueryMetricCustom[]}
     */
    get metricsCustom() {
        return this._query[QueryKey.METRICS_CUSTOM];
    }
    
    /**
     *
     * @param {IQueryMetricCustomInfo[]} metricCustomInfos
     */
    set metricsCustom(metricCustomInfos) {
        this._query[QueryKey.METRICS_CUSTOM] = metricCustomInfos.map(queryMetricCustomCreate);
    }
    
    /**
     * @param {string} param
     * @return {string}
     */
    getParamName(param) {
        const { metrics, dimensions, fields } = this;
        const metricsCustomNames = this._getMetricCustomNames();
        return [...metrics, ...dimensions, ...metricsCustomNames, ...fields].includes(param) ? param : null;
    }
    
    /**
     *
     * @return {{[p: string]: string}}
     */
    createResultQueryParamMap() {
        return Object.fromEntries(
            [...this.order, ...this._getMetricCustomNames()].map((param) => [param, this.getParamName(param)])
        );
    }
    
    /**
     * @return {string[]}
     */
    _getMetricCustomNames() {
        return this.metricsCustom.map(({ name }) => name);
    }
    
    /**
     * @param {IQueryMetricCustomInfo} metricCustom
     * @param {boolean} [forceUpdate=true]
     * @returns {boolean}
     */
    metricCustomAdd({ name, field, func }, forceUpdate = true) {
        const { _query: query } = this;
        try {
            const metric = queryMetricCustomCreate({ name, field, func });
            this._query = queryMetricCustomAdd({ query, metric }, forceUpdate);
            return true;
        } catch {
            return false;
        }
    }
    
    /**
     * @param {IQueryMetricCustomInfo['name']} name
     * @returns {boolean}
     */
    metricCustomRemove(name) {
        const { _query: query } = this;
        if (queryMetricCustomExists({ query, metric: name })) {
            this._query = queryMetricCustomRemove({ query, metric: name });
            return true;
        }
        return false;
    }
    
    /**
     * @returns {void}
     */
    metricCustomRemoveAll() {
        const { raw: query } = this;
        this._query = queryMetricCustomRemoveAll({ query });
    }
}

/**
 *
 * @param {import('./types').IDatasetQuery} query
 * @return {QueryModel}
 * @constructor
 */
export const DatasetQueryModelFactory = (query) => new QueryModel(query);

/**
 *
 * @param {import('./types').IDatasetQuery} query
 * @return {QueryModelWithMetricsCustom}
 * @constructor
 */
export const DatasetQueryModelWithMetricsCustomFactory = (query) => new QueryModelWithMetricsCustom(query);

/**
 *
 * @type {{ CUSTOM_METRICS: IQueryModelStrategy, DEFAULT: IQueryModelStrategy }}
 */
export const QueryModelsStrategies = {
    DEFAULT: {
        build(query) {
            return new QueryModel(query);
        }
    },
    CUSTOM_METRICS: {
        build(query) {
            return new QueryModelWithMetricsCustom(query);
        }
    }
}
